// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { formatDate } from "@transformers/date";
import { BoxField, Button, GSGrid, SwitchButton, Checkbox, Text, View } from "@components";
import type {
  EmailControlSettings,
  EmailDomain,
} from "@modules/service/reducer/email-controls/types";

type Props = $Exact<{
  list: Array<EmailDomain>,
  settings: ?EmailControlSettings,
  onUpdateEmailSettings: Function,
  onAdd: Function,
  ...PropsFromLocalize,
}>;

type State = $Exact<{
  checkedEmailDomains: Array<EmailDomain>,
  isSectionOpen: boolean,
}>;

export function EmailDomains({ list, settings, t, onUpdateEmailSettings, onAdd }: Props) {
  const [checkedEmailDomains, setCheckedEmailDomains] = useState([]);
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const enabled = (settings || {}).emailDomainRestrictionEnabled;
  const columns = [
    {
      name: t("EMAIL_DOMAIN"),
      key: "domain",
      align: "left",
      renderCell: ({ row }: { row: EmailDomain }) => GSGrid.render.ellipsis(row.value),
    },
    {
      name: t("STATUS"),
      key: "isEnabled",
      align: "center",
      renderCell: ({ row }: { row: EmailDomain }) => (
        <div className={row.isEnabled ? "active-badge" : "inactive-badge"}>
          {row.isEnabled ? t("ACTIVE") : t("INACTIVE")}
        </div>
      ),
    },
    {
      name: t("DATE_ADDED"),
      key: "startTime",
      renderCell: ({ row }: { row: EmailDomain }) => formatDate(row.activeFrom),
    },
    {
      name: t("DATE_DISABLED"),
      key: "endTime",
      renderCell: ({ row }: { row: EmailDomain }) => formatDate(row.activeTo),
    },
  ];

  function handleSelectedRows(selectedRows: [string]) {
    setCheckedEmailDomains(selectedRows);
  }

  function toggleSectionOption() {
    setIsSectionOpen(!isSectionOpen);
  }

  function updateEnabled(emailDomainRestrictionEnabled: boolean) {
    onUpdateEmailSettings({ ...settings, emailDomainRestrictionEnabled });
    if (!emailDomainRestrictionEnabled) {
      setCheckedEmailDomains([]);
    }
  }

  function restoreEmailDomains() {
    onUpdateEmailSettings({
      ...settings,
      emailDomainRestrictionWhitelist: list.map((record: EmailDomain) => {
        if (checkedEmailDomains.includes(record.value)) {
          return {
            ...record,
            isEnabled: true,
            activeTo: "",
          };
        } else return record;
      }),
    });
    deselectAllHandler();
  }

  function disableEmailDomains() {
    onUpdateEmailSettings({
      ...settings,
      emailDomainRestrictionWhitelist: list.map((record: EmailDomain) => {
        if (checkedEmailDomains.includes(record.value)) {
          return {
            ...record,
            isEnabled: false,
            activeTo: new Date().toISOString(),
          };
        } else return record;
      }),
    });
    deselectAllHandler();
  }

  function deselectAllHandler() {
    setCheckedEmailDomains([]);
  }

  return (
    <View gap={4}>
      <View direction="row" justifyContent="space-between" alignItems="center">
        <Text type="h5">{t("EMAIL_DOMAINS")}</Text>
        {enabled && (
          <Button materialIcon="add_box" className="add-ip-address" onClick={onAdd}>
            {t("ADD")}
          </Button>
        )}
      </View>
      {settings && (
        <BoxField title={t("RESTRICTED_LIST")} description={t("RESTRICTED_LIST_DESCRIPTION")}>
          <SwitchButton active={enabled} onChange={updateEnabled} />
        </BoxField>
      )}
      {enabled && (
        <GSGrid
          onSelectedRowsChange={selectedRows => handleSelectedRows(selectedRows)}
          selectedRows={checkedEmailDomains}
          rowKeyGetter={row => row.value}
          rows={list}
          columns={columns}
        />
      )}
      {checkedEmailDomains.length !== 0 && enabled && (
        <View direction="row" gap={2} alignItems="center">
          <Text color="orange">
            {t("RECORDS_SELECTED", {
              numberOfRecords: checkedEmailDomains.length,
            })}
          </Text>
          <Button
            size="small"
            color="positive"
            className="mr-3 restore-email-domains"
            onClick={restoreEmailDomains}
          >
            {t("RESTORE")}
          </Button>
          <Button
            size="small"
            color="negative"
            className="disable-email-domains"
            onClick={disableEmailDomains}
          >
            {t("DISABLE")}
          </Button>
          <Button size="small" color="secondary" onClick={deselectAllHandler}>
            {t("DESELECT_ALL")}
          </Button>
        </View>
      )}
    </View>
  );
}

export default compose(localize())(EmailDomains);
